import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const Deposit: React.FC = () => {
  const colors = getThemeColors('dark');

  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [loading, setLoading] = useState(false);

  const paymentMethods = [
    { id: 'mpesa', name: 'M-Pesa', icon: '📱' },
    { id: 'bank', name: 'Bank Transfer', icon: '🏦' },
    { id: 'card', name: 'Credit/Debit Card', icon: '💳' },
  ];

  const handleDeposit = async () => {
    const depositAmount = parseFloat(amount) || 0;

    if (!depositAmount || depositAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      // Mock API call - in real app this would call actual API
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert(`Deposit of KES ${depositAmount.toLocaleString()} initiated via ${paymentMethod.toUpperCase()}`);
      setAmount('');
    } catch (error) {
      console.error('Deposit failed:', error);
      alert('Deposit failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    form: {
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.xl,
      ...getShadowStyle('md'),
    },
    label: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
      marginTop: spacing.md,
      display: 'block',
    },
    amountInput: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      outline: 'none',
      boxSizing: 'border-box',
    },
    paymentMethod: {
      display: 'flex',
      alignItems: 'center',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginBottom: spacing.sm,
      border: `2px solid ${paymentMethod === 'mpesa' ? colors.primary : colors.border}`,
      backgroundColor: paymentMethod === 'mpesa' ? colors.primary + '20' : colors.surface,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    methodIcon: {
      fontSize: 24,
      marginRight: spacing.sm,
    },
    methodText: {
      flex: 1,
      fontSize: typography.fontSize.base,
      color: paymentMethod === 'mpesa' ? colors.text : colors.textSecondary,
      fontWeight: paymentMethod === 'mpesa' ? '600' : 'normal',
    },
    radioIcon: {
      fontSize: 20,
      color: paymentMethod === 'mpesa' ? colors.primary : colors.textSecondary,
    },
    depositButton: {
      width: '100%',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      backgroundColor: colors.primary,
      border: 'none',
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      marginTop: spacing.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      transition: 'all 0.2s ease',
    },
    loadingIcon: {
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Deposit Money"
        subtitle="Add funds to your wallet securely"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.form}>
        <label style={styles.label}>Amount (KES)</label>
        <input
          type="number"
          style={styles.amountInput}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />

        <label style={styles.label}>Payment Method</label>
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            style={{
              ...styles.paymentMethod,
              borderColor: paymentMethod === method.id ? colors.primary : colors.border,
              backgroundColor: paymentMethod === method.id ? colors.primary + '20' : colors.surface,
            }}
            onClick={() => setPaymentMethod(method.id)}
          >
            <span style={styles.methodIcon}>{method.icon}</span>
            <span style={styles.methodText}>{method.name}</span>
            <span style={styles.radioIcon}>
              {paymentMethod === method.id ? '⭕' : '⚪'}
            </span>
          </div>
        ))}

        <button
          style={{
            ...styles.depositButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleDeposit}
          disabled={loading}
        >
          {loading && <span style={styles.loadingIcon}>🔄</span>}
          {loading ? 'Processing...' : `Deposit KES ${amount || '0.00'}`}
        </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deposit;